/**
 * Client-side performance reporter.
 *
 * Buffers Web Vitals (LCP / FCP / INP / CLS / TTFB) and Navigation
 * Timing snapshots, flushing batched POSTs to ``/api/v1/perf/report``
 * via ``navigator.sendBeacon`` on visibility-hidden and on hard
 * navigations, plus a 10 s timer / 30-entry cap whichever first.
 *
 * Implementation uses native ``PerformanceObserver`` (LCP / FCP / CLS /
 * INP via ``event-timing``) plus ``PerformanceNavigationTiming`` —
 * intentionally no ``web-vitals`` npm dependency. If a metric isn't
 * supported on the user's browser we silently skip it; the harness
 * tolerates partial coverage.
 *
 * Best-effort: any error in the reporter (network, observer init) is
 * swallowed — perf instrumentation must never break the page.
 */

import { getActiveProfiler } from '@/lib/page-profiler';

export type PerfEntry =
  | {
      kind: 'web_vital';
      path: string;
      ts: number;
      name: 'LCP' | 'FCP' | 'INP' | 'CLS' | 'TTFB';
      value: number;
    }
  | {
      kind: 'navigation';
      path: string;
      ts: number;
      navigation: {
        type: 'navigate' | 'reload' | 'back_forward' | 'prerender';
        ttfb_ms: number;
        dom_content_loaded_ms: number;
        dom_interactive_ms: number;
        load_event_end_ms: number;
        transfer_size_bytes?: number;
      };
    };

const WEB_VITAL_METRIC: Record<string, string> = {
  LCP: 'lcp',
  FCP: 'fcp',
  INP: 'inp',
  CLS: 'cls',
  TTFB: 'ttfb',
};

const BATCH_MAX = 30;
const FLUSH_INTERVAL_MS = 10_000;
const ENDPOINT = '/api/v1/perf/report';

class PerfReporter {
  private buffer: PerfEntry[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private started = false;

  start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;

    try {
      this.observeWebVitals();
      this.captureNavigation();
    } catch {
      /* swallow — never break the page */
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush(true);
    });
    window.addEventListener('pagehide', () => this.flush(true));
    this.timer = setInterval(() => this.flush(false), FLUSH_INTERVAL_MS);
  }

  add(entry: PerfEntry): void {
    this.buffer.push(entry);
    if (this.buffer.length >= BATCH_MAX) this.flush(false);
    // Sprint1 A1 — forward Web Vitals into the new ``perf_page_events``
    // table via the currently mounted profiler so the dashboard can
    // pivot LCP/INP/CLS by screen_key + page_load_id alongside our
    // own phase / widget metrics. Backwards-safe: when no profiler is
    // active (e.g. on a non-instrumented route) the entry still lands
    // in the legacy ring buffer above.
    if (entry.kind === 'web_vital') {
      const metric = WEB_VITAL_METRIC[entry.name];
      if (metric !== undefined) {
        try {
          getActiveProfiler()?.record({
            metric,
            value: entry.value,
          });
        } catch {
          /* never break the page */
        }
      }
    }
  }

  private flush(beacon: boolean): void {
    if (this.buffer.length === 0) return;
    const batch = this.buffer.slice(0, BATCH_MAX);
    this.buffer = this.buffer.slice(BATCH_MAX);
    const body = JSON.stringify({ entries: batch });
    try {
      if (beacon && navigator.sendBeacon) {
        navigator.sendBeacon(
          ENDPOINT,
          new Blob([body], { type: 'application/json' }),
        );
      } else {
        void fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {
          /* drop */
        });
      }
    } catch {
      /* drop */
    }
  }

  private observeWebVitals(): void {
    if (typeof PerformanceObserver === 'undefined') return;
    const path = () => window.location.pathname;
    const now = () => Date.now();

    // LCP — last entry wins, captured on observer disconnect (page hide)
    let lastLcp = 0;
    try {
      const lcpObs = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          lastLcp = (e as PerformanceEntry & { startTime: number }).startTime;
        }
      });
      lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });
      addEventListener('pagehide', () => {
        if (lastLcp > 0) {
          this.add({
            kind: 'web_vital',
            path: path(),
            ts: now(),
            name: 'LCP',
            value: lastLcp,
          });
        }
      });
    } catch {
      /* unsupported */
    }

    // FCP — first paint
    try {
      const fcpObs = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          if (e.name === 'first-contentful-paint') {
            this.add({
              kind: 'web_vital',
              path: path(),
              ts: now(),
              name: 'FCP',
              value: e.startTime,
            });
            fcpObs.disconnect();
          }
        }
      });
      fcpObs.observe({ type: 'paint', buffered: true });
    } catch {
      /* unsupported */
    }

    // CLS — cumulative shifts (sum of unexpected layout-shift values)
    let cls = 0;
    try {
      const clsObs = new PerformanceObserver((list) => {
        for (const e of list.getEntries() as Array<
          PerformanceEntry & { value: number; hadRecentInput?: boolean }
        >) {
          if (!e.hadRecentInput) cls += e.value;
        }
      });
      clsObs.observe({ type: 'layout-shift', buffered: true });
      addEventListener('pagehide', () => {
        this.add({
          kind: 'web_vital',
          path: path(),
          ts: now(),
          name: 'CLS',
          value: cls,
        });
      });
    } catch {
      /* unsupported */
    }

    // INP — worst interaction's processing duration (approximation)
    let worstInp = 0;
    try {
      const inpObs = new PerformanceObserver((list) => {
        for (const e of list.getEntries() as Array<
          PerformanceEntry & { duration: number; interactionId?: number }
        >) {
          if (e.interactionId && e.duration > worstInp) worstInp = e.duration;
        }
      });
      inpObs.observe({ type: 'event', buffered: true, durationThreshold: 16 } as PerformanceObserverInit);
      addEventListener('pagehide', () => {
        if (worstInp > 0) {
          this.add({
            kind: 'web_vital',
            path: path(),
            ts: now(),
            name: 'INP',
            value: worstInp,
          });
        }
      });
    } catch {
      /* unsupported */
    }
  }

  private captureNavigation(): void {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (!nav) return;
    const type = (nav.type as PerfEntry extends { navigation: infer N }
      ? N extends { type: infer T }
        ? T
        : never
      : never) ?? 'navigate';
    this.add({
      kind: 'navigation',
      path: window.location.pathname,
      ts: Date.now(),
      navigation: {
        type: type as 'navigate' | 'reload' | 'back_forward' | 'prerender',
        ttfb_ms: nav.responseStart,
        dom_content_loaded_ms: nav.domContentLoadedEventEnd,
        dom_interactive_ms: nav.domInteractive,
        load_event_end_ms: nav.loadEventEnd,
        transfer_size_bytes: nav.transferSize,
      },
    });
    // TTFB as its own web vital for convenience
    this.add({
      kind: 'web_vital',
      path: window.location.pathname,
      ts: Date.now(),
      name: 'TTFB',
      value: nav.responseStart,
    });
  }
}

let _singleton: PerfReporter | null = null;

export function getPerfReporter(): PerfReporter {
  if (_singleton === null) _singleton = new PerfReporter();
  return _singleton;
}
